import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeftIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, TextAreaField, DisplayField } from '../components/FormComponents';
import { mockProducts, masterDataSets } from '../data/mockData';
export function StockTransferForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    transferNo: 'TRF-AUTO-GEN',
    date: new Date().toISOString().split('T')[0],
    product: '',
    productCode: '',
    fromWarehouse: '',
    toWarehouse: '',
    quantity: '',
    unitOfMeasure: '',
    reason: '',
    performedBy: '',
    remarks: ''
  });
  const handleProductChange = (productId: string) => {
    const selectedProduct = mockProducts.find(p => p.id === productId);
    if (selectedProduct) {
      setFormData(prev => ({
        ...prev,
        product: productId,
        productCode: selectedProduct.productCode,
        unitOfMeasure: selectedProduct.unitOfMeasure
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        product: '',
        productCode: '',
        unitOfMeasure: ''
      }));
    }
  };
  const handleSubmit = () => {
    if (formData.fromWarehouse === formData.toWarehouse && formData.fromWarehouse !== '') {
      alert('Source and destination warehouses cannot be the same.');
      return;
    }
    alert('Stock Transfer submitted successfully!');
    navigate('/stock/transfer');
  };
  const warehouseOptions = (masterDataSets.warehouses || []).filter(wh => wh.status === 'Active').map(wh => ({
    value: wh.name,
    label: `${wh.name} (${wh.code})`
  }));
  const productOptions = mockProducts.map(p => ({
    value: p.id,
    label: `${p.productName} (${p.productCode})`
  }));
  const reasonOptions = [{
    value: 'Restock',
    label: 'Restock'
  }, {
    value: 'Consolidation',
    label: 'Consolidation'
  }, {
    value: 'Project Requirement',
    label: 'Project Requirement'
  }, {
    value: 'Office Setup',
    label: 'Office Setup'
  }, {
    value: 'Maintenance',
    label: 'Maintenance'
  }, {
    value: 'Other',
    label: 'Other'
  }];
  return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-2644">
      <div className="flex items-center gap-3" data-id="element-2645">
        <div className="p-3 bg-indigo-100 rounded-lg" data-id="element-2646">
          <ArrowRightLeftIcon className="h-6 w-6 text-indigo-600" data-id="element-2647" />
        </div>
        <div data-id="element-2648">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2649">
            Create Stock Transfer
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2650">
            Transfer inventory between warehouses.
          </p>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-2651">
        <FormSection title="Transfer Details" data-id="element-2652">
          <DisplayField label="Transfer No" value={formData.transferNo} data-id="element-2653" />
          <TextField label="Date" type="date" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} required data-id="element-2654" />
          <SelectField label="From Warehouse" value={formData.fromWarehouse} onChange={e => setFormData({
          ...formData,
          fromWarehouse: e.target.value
        })} options={warehouseOptions} required data-id="element-2655" />
          <SelectField label="To Warehouse" value={formData.toWarehouse} onChange={e => setFormData({
          ...formData,
          toWarehouse: e.target.value
        })} options={warehouseOptions} required data-id="element-2656" />
          {formData.fromWarehouse && formData.toWarehouse && formData.fromWarehouse === formData.toWarehouse && <div className="md:col-span-2" data-id="element-2657">
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-id="element-2658">
                  ⚠ Source and destination warehouses cannot be the same.
                </p>
              </div>}
        </FormSection>

        <FormSection title="Product Information" data-id="element-2659">
          <SelectField label="Product" value={formData.product} onChange={e => handleProductChange(e.target.value)} options={productOptions} required data-id="element-2660" />
          <DisplayField label="Product Code" value={formData.productCode || '—'} data-id="element-2661" />
          <TextField label="Quantity" type="number" min="1" value={formData.quantity} onChange={e => setFormData({
          ...formData,
          quantity: e.target.value
        })} required data-id="element-2662" />
          <DisplayField label="Unit of Measure" value={formData.unitOfMeasure || '—'} data-id="element-2663" />
        </FormSection>

        <FormSection title="Reason & Additional Info" data-id="element-2664">
          <SelectField label="Reason" value={formData.reason} onChange={e => setFormData({
          ...formData,
          reason: e.target.value
        })} options={reasonOptions} required data-id="element-2665" />
          <TextField label="Performed By" value={formData.performedBy} onChange={e => setFormData({
          ...formData,
          performedBy: e.target.value
        })} placeholder="Name of person initiating transfer" required data-id="element-2666" />
          <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
          ...formData,
          remarks: e.target.value
        })} rows={3} placeholder="Additional notes about this transfer..." data-id="element-2667" />
        </FormSection>

        {/* Transfer Summary */}
        {formData.product && formData.fromWarehouse && formData.toWarehouse && formData.quantity && <div className="mb-8 bg-indigo-50 border border-indigo-200 rounded-lg p-4" data-id="element-2668">
              <h4 className="text-sm font-medium text-indigo-900 mb-3" data-id="element-2669">
                Transfer Summary
              </h4>
              <div className="flex items-center gap-4 text-sm" data-id="element-2670">
                <div className="text-center" data-id="element-2671">
                  <p className="text-xs text-indigo-600 font-medium" data-id="element-2672">From</p>
                  <p className="font-semibold text-gray-900" data-id="element-2673">
                    {formData.fromWarehouse}
                  </p>
                </div>
                <ArrowRightLeftIcon className="h-5 w-5 text-indigo-400 flex-shrink-0" data-id="element-2674" />
                <div className="text-center" data-id="element-2675">
                  <p className="text-xs text-indigo-600 font-medium" data-id="element-2676">To</p>
                  <p className="font-semibold text-gray-900" data-id="element-2677">
                    {formData.toWarehouse}
                  </p>
                </div>
                <div className="ml-auto text-right" data-id="element-2678">
                  <p className="text-xs text-indigo-600 font-medium" data-id="element-2679">
                    Quantity
                  </p>
                  <p className="font-semibold text-gray-900" data-id="element-2680">
                    {formData.quantity} {formData.unitOfMeasure}
                  </p>
                </div>
              </div>
            </div>}

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2681">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2682">
            Cancel
          </button>
          <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-2683">
            Save as Draft
          </button>
          <button type="button" onClick={handleSubmit} disabled={formData.fromWarehouse === formData.toWarehouse && formData.fromWarehouse !== ''} className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${formData.fromWarehouse === formData.toWarehouse && formData.fromWarehouse !== '' ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-2684">
            Submit Transfer
          </button>
        </div>
      </form>
    </div>;
}